/**
 * A1Icons — custom inline-SVG icon kit specific to screen-enclosure work.
 *
 * Every icon:
 *  - 24×24 viewBox
 *  - Pure inline SVG, no dependencies, no network requests
 *  - Single-weight stroke (1.6px), `currentColor` for stroke
 *  - Decorative-only (parents control `aria-hidden`); no labels baked in
 *  - Renders crisply 14–48px
 *
 * Icons here are A1-Screening-specific — they communicate the trade
 * (torn mesh, pool cage frame, lanai roof, screen door, condo balcony
 * panel, storm-affected cage, mesh roll, Z-bar trim) so the site reads
 * unmistakably as a screen enclosure company, not a generic contractor.
 *
 * Each component accepts:
 *   - size (number)   default 22
 *   - strokeWidth     default 1.6
 *   - className       optional extra class
 *
 * Usage:
 *   import { TornMesh } from "@/components/ui/icons/A1Icons";
 *   <TornMesh size={20} />
 */

const baseProps = (size, strokeWidth, className) => ({
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    "aria-hidden": "true",
});

/** Torn mesh — a screen panel with a tear running through it */
export function TornMesh({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            <rect x="3" y="3" width="18" height="18" rx="1.5" />
            <path d="M3 8H21M3 13H21M3 18H21" opacity="0.55" />
            <path d="M8 3V21M13 3V21M18 3V21" opacity="0.55" />
            {/* the tear */}
            <path d="M9 6.5L11 10L9.5 13.5L12 16L10.5 19.5" stroke="currentColor" strokeWidth={strokeWidth + 0.4} opacity="1" />
        </svg>
    );
}

/** Pool cage frame corner — two aluminum beams meeting at a corner joint */
export function CageFrameCorner({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            <path d="M4 4H20V20" />
            <path d="M4 4V20H20" opacity="0" />
            {/* inner offset beam (creates the I-beam frame look) */}
            <path d="M7.5 7.5H20" opacity="0.7" />
            <path d="M7.5 7.5V20" opacity="0.7" />
            {/* corner joint plate */}
            <rect x="3" y="3" width="3" height="3" />
            {/* faint mesh inside the cage */}
            <path d="M9 11.5L11 14L9.5 16.5" opacity="0.55" />
            <path d="M13 11.5L15 14L13.5 16.5" opacity="0.55" />
        </svg>
    );
}

/** Lanai / patio roof line — a sloped roof over a screened lanai */
export function LanaiRoof({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            {/* sloped roof */}
            <path d="M2 10L12 4L22 10" />
            {/* lanai outline */}
            <path d="M4 10V20H20V10" />
            {/* screen-mesh inside the lanai opening */}
            <path d="M8 12V20M12 12V20M16 12V20" opacity="0.5" />
            <path d="M5 14H19M5 17H19" opacity="0.5" />
            {/* ground line */}
            <path d="M3 20H21" opacity="0.6" />
        </svg>
    );
}

/** Screen door — full-height door with kick plate, handle, and mesh */
export function ScreenDoor({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            <rect x="5" y="2.5" width="14" height="19" rx="1" />
            {/* kick plate at the bottom */}
            <path d="M5 17H19" />
            {/* mesh panel above */}
            <path d="M8.5 5V16M12 5V16M15.5 5V16" opacity="0.5" />
            <path d="M5.5 8H18.5M5.5 11H18.5M5.5 14H18.5" opacity="0.5" />
            {/* handle */}
            <circle cx="8" cy="13" r="0.6" fill="currentColor" />
        </svg>
    );
}

/** Condo balcony panel — a railing + small screen panel */
export function CondoPanel({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            {/* outer balcony frame */}
            <rect x="3" y="5" width="18" height="14" rx="1" />
            {/* horizontal railing */}
            <path d="M3 11H21" />
            <path d="M3 16H21" opacity="0.7" />
            {/* vertical balusters / mesh frame */}
            <path d="M8 5V19M12 5V19M16 5V19" opacity="0.7" />
            {/* ground / floor line */}
            <path d="M2 19H22" opacity="0.6" />
            {/* sky/sun above the panel */}
            <circle cx="18" cy="3.5" r="1.2" opacity="0.55" />
        </svg>
    );
}

/** Storm cage — a pool cage silhouette with a wind swirl behind it */
export function StormCage({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            {/* wind swirl */}
            <path d="M3 7Q8 5 14 7" opacity="0.55" />
            <path d="M3 10Q9 8 15 10" opacity="0.55" />
            {/* cage frame (rounded peaked) */}
            <path d="M5 20V13L12 8L19 13V20" />
            <path d="M5 20H19" />
            {/* mesh inside the cage */}
            <path d="M8 13V20M12 11V20M16 13V20" opacity="0.5" />
            <path d="M5.5 16H18.5" opacity="0.5" />
        </svg>
    );
}

/** Mesh roll — a roll of fresh screen mesh on a spool */
export function MeshRoll({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            {/* roll outer */}
            <ellipse cx="9" cy="12" rx="6" ry="7" />
            {/* roll inner core */}
            <ellipse cx="9" cy="12" rx="1.6" ry="2" />
            {/* faint inner roll line */}
            <ellipse cx="9" cy="12" rx="3.5" ry="4.2" opacity="0.55" />
            {/* unrolled mesh strip extending right */}
            <path d="M15 9.5L22 9.5L22 14.5L15 14.5" />
            {/* mesh weave on the strip */}
            <path d="M16 9.5V14.5M18 9.5V14.5M20 9.5V14.5" opacity="0.55" />
            <path d="M15 12H22" opacity="0.55" />
        </svg>
    );
}

/** Z-bar — screen door trim, side profile of a Z-shaped extrusion */
export function ZBar({ size = 22, strokeWidth = 1.6, className }) {
    return (
        <svg {...baseProps(size, strokeWidth, className)}>
            {/* Z extrusion profile */}
            <path d="M5 4L19 4L19 11L13 11L13 20L5 20" />
            {/* second profile line giving depth */}
            <path d="M5 7L16 7L16 14L8 14L8 20" opacity="0.55" />
            {/* fastener dots along the Z */}
            <circle cx="7" cy="5.5" r="0.7" fill="currentColor" />
            <circle cx="7" cy="18.5" r="0.7" fill="currentColor" />
            <circle cx="17" cy="5.5" r="0.7" fill="currentColor" />
        </svg>
    );
}

/**
 * Lookup table — service slug → preferred A1 icon for cards.
 * Falls back to CageFrameCorner if a slug isn't mapped.
 */
export const A1_SERVICE_ICONS = {
    "pool-cage-repair": CageFrameCorner,
    "pool-rescreening": MeshRoll,
    "screen-door-repair": ScreenDoor,
    "condo-screen-repair": CondoPanel,
    "lanai-patio-repair": LanaiRoof,
    "lanai-screen-repair": LanaiRoof,
    "screen-enclosure-repair": CageFrameCorner,
    "storm-damage-repair": StormCage,
    "storm-damage": StormCage,
    "screen-door-hardware": ZBar,
    "z-bar-trim": ZBar,
};

export function iconForSlug(slug) {
    return A1_SERVICE_ICONS[slug] || CageFrameCorner;
}
